import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Badge, Breadcrumb } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { usePanel } from '@/context/PanelProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { Environment, useEnvironments } from '@/hooks/useEnvironments';
import {
  getResourceNavOption,
  NavigationResourceType,
} from '@/panel/ResourcesFilter/selectedResource';
import { truthy } from '@/util/array';
import { pluralizeResourceType } from '@/util/resourceType';

export interface UseProjectBreadcrumbsOptions {
  /**
   * Additional breadcrumb items to append after the project breadcrumb
   */
  additionalItems?: Breadcrumb[];
}

/**
 * Hook that generates breadcrumb objects for project-based pages
 * with environment switcher support when enabled via feature flag
 */
export const useProjectBreadcrumbs = (options: UseProjectBreadcrumbsOptions = {}) => {
  const { accountId, accountName, projectName } = useDbtCloudProject();
  const { isSinglePaneView } = usePanel();
  const projectScope = useUrlProjectParams();
  const projectUrl = useLinkToExplorer('project')({ ...projectScope });
  const getMultiProjectLink = useLinkToExplorer('multiProject');

  const { currentEnvironment } = useEnvironments();

  const breadcrumbs = useMemo(() => {
    const items: Breadcrumb[] = [
      ...(isSinglePaneView
        ? []
        : [
            {
              text: accountName,
              href: getMultiProjectLink({
                accountId,
                tab: 'projects',
              }),
              badge: currentEnvironment?.badge,
              badgeType: currentEnvironment?.type,
            },
          ]),
      {
        text: projectName,
        href: projectUrl,
      },

      ...(options.additionalItems || []),
    ];

    return items.filter(truthy);
  }, [
    isSinglePaneView,
    accountName,
    accountId,
    getMultiProjectLink,
    projectName,
    projectUrl,
    currentEnvironment,
    options.additionalItems,
  ]);
  return breadcrumbs;
};

type ResourceTypeBreadcrumbProps = {
  resourceType: NavigationResourceType | 'package' | string;
  includeLink?: boolean;
};

/**
 * A hook that generates a breadcrumb item for a resource type
 * Used when displaying the resource type in the breadcrumbs
 */
export const useResourceTypeBreadcrumb = ({
  resourceType,
  includeLink = true,
}: ResourceTypeBreadcrumbProps): Breadcrumb | undefined => {
  const navigationResourceType = getResourceNavOption(resourceType);
  const projectScope = useUrlProjectParams();
  const getResourceUrl = useLinkToExplorer('resourceFilter');

  return useMemo(() => {
    if (!navigationResourceType) return undefined;

    const pluralized = pluralizeResourceType(navigationResourceType);
    const capitalizedResources =
      pluralized.charAt(0).toUpperCase() + pluralized.slice(1);

    return {
      text: capitalizedResources,
      href: includeLink
        ? getResourceUrl({
            ...projectScope,
            resourceType: navigationResourceType,
          })
        : undefined,
    };
  }, [navigationResourceType, includeLink, getResourceUrl, projectScope]);
};

export const useEnvironmentSwitcherBreadcrumb = () => {
  const navigate = useNavigate();
  const { environments, currentEnvironment } = useEnvironments();
  const [selectedEnvironment, setSelectedEnvironment] = useState<
    Environment | undefined
  >(currentEnvironment);

  useEffect(() => {
    setSelectedEnvironment(currentEnvironment);
  }, [currentEnvironment]);

  if (environments.length === 0) {
    return {
      items: [],
      onClick: () => {},
    };
  }

  return {
    items: environments.map((env) => ({
      label: env.label,
      description: env.badge ? (
        <span className="flex items-center gap-2">
          {env.badge && <Badge text={env.badge} size="xxs" type={env.type} />}
        </span>
      ) : undefined,
      isSelected: env.label === selectedEnvironment?.label,
      value: env.label,
    })),
    onClick: (value: string | number) => {
      const env = environments.find((e) => e.label === value);
      if (env) {
        setSelectedEnvironment(env);
        navigate(env.path);
      }
    },
    className: 'w-56',
  };
};
